import { type DictationHotkeyPlatform, MOD_ORDER, stripChordSides } from '@heidi/dictate';

/**
 * Reserved combos per platform. Each entry is the ordered canonical-name array
 * for the chord (modifiers first, terminal key last). Modifier order inside the
 * stored chord follows `encodeChord` (Fn/Windows → Control → Option/Alt → Shift
 * → Command), so the lookup is just an array-equality check.
 *
 * See `docs/desktop/dictation/HOTKEY.md` → "Reserved combos".
 */

// Reserved-combo entries use the post-side-stripped W3C root names
// (`Meta` instead of `Cmd`/`Win`, `Alt` instead of `Option`) and the W3C
// non-modifier names (`KeyQ`, `Digit3`, etc.). At lookup time, the input
// chord is normalised by `stripChordSides()` so `MetaLeft` and `MetaRight`
// both compare equal to `Meta`.

// Hard-block list per HOTKEY.md → "Hard-block reserved list and override-on-bind".
// Tight, intentional set: blast-radius editing chords, strands shortcuts (app
// switcher / quit / force-quit), OS-wins shortcuts (lock screen). Everything
// not on this list, not on ALWAYS_BLOCKED_TERMINAL_KEYS, and not a bare
// BARE_BLOCKED_TERMINAL_KEYS press is bindable.
const macReservedList: string[][] = [
  // Blast radius — universal editing shortcuts.
  ['Meta', 'KeyC'],
  ['Meta', 'KeyV'],
  ['Meta', 'KeyX'],
  ['Meta', 'KeyZ'],
  ['Meta', 'KeyA'],
  // Strands — app switcher, quit, force-quit.
  ['Meta', 'Tab'],
  ['Meta', 'KeyQ'],
  ['Meta', 'Alt', 'Escape'],
  // OS wins — lock screen handler fires regardless of event tap.
  ['Control', 'Meta', 'KeyQ'],
];

const windowsReservedList: string[][] = [
  // Blast radius — universal editing shortcuts.
  ['Control', 'KeyC'],
  ['Control', 'KeyV'],
  ['Control', 'KeyX'],
  ['Control', 'KeyZ'],
  ['Control', 'KeyA'],
  // Strands — app switcher, quit, Task Manager.
  ['Alt', 'Tab'],
  ['Alt', 'F4'],
  ['Control', 'Shift', 'Escape'],
  // OS wins — Secure Attention Sequence and lock screen are routed below the
  // hook; included for UI completeness so users get the reserved-state
  // message instead of recording a chord that won't ever fire.
  ['Control', 'Alt', 'Delete'],
  ['Meta', 'KeyL'],
];

/**
 * Terminal keys that are never bindable as the terminal key of any chord,
 * even with a modifier. Applies to both platforms.
 *
 *  - `CapsLock`: state-toggle key — macOS only reports it on the toggle edge,
 *    not on hold, so chord engagement / disengagement transitions don't align
 *    with physical press / release.
 *  - `ArrowUp/Down/Left/Right`: caret navigation and list traversal. Binding
 *    any arrow-ending chord would suppress arrow behaviour in every focused
 *    field / list / menu on the machine while Heidi is running.
 */
const ALWAYS_BLOCKED_TERMINAL_KEYS: ReadonlySet<string> = new Set<string>([
  'CapsLock',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

/**
 * Terminal keys that are blocked **only when used alone** (chord length === 1).
 * With any modifier they are bindable. Applies to both platforms.
 *
 *  - `Enter` / `NumpadEnter`: bare Enter is the universal submit/commit and
 *    would strand the user in any form or dialog; `Cmd+Enter` / `Ctrl+Enter`
 *    etc. are bindable.
 *  - `Escape`: bare Escape is universal dismiss + conflicts with widget
 *    Escape-to-dismiss; modifier+Escape is bindable.
 *  - `Tab`: bare Tab is universal next-field / focus traversal; modifier+Tab
 *    is bindable.
 *  - `Backspace` / `Delete`: bare keys are destructive — accidental data loss
 *    in any text field. Both W3C names are listed because the macOS main
 *    delete key is `Backspace` and the forward-delete is `Delete`; on Windows
 *    they are Backspace and Delete respectively. With a modifier (e.g.
 *    `Cmd+Backspace`) they're bindable.
 */
const BARE_BLOCKED_TERMINAL_KEYS: ReadonlySet<string> = new Set<string>([
  'Enter',
  'NumpadEnter',
  'Escape',
  'Tab',
  'Backspace',
  'Delete',
]);

const NEUTRAL_MODIFIERS = new Set<string>(['Fn', 'Control', 'Alt', 'Shift', 'Meta']);

/**
 * Windows modifier-only chords that include Alt or Win (Meta). Lone Alt / Win
 * trigger menu-bar / Start-menu OS behaviour; the hook's `SwallowModifier`
 * lone-action suppression ([#10901](https://github.com/oscerai/scribe-fe-v2/pull/10901))
 * cannot sustain reliable hold-to-talk. Modifier-only Alt+Shift etc. share the
 * same failure mode. Ctrl/Shift-only modifier chords remain bindable.
 */
const WINDOWS_MODIFIER_ONLY_SYSTEM_MODIFIERS: ReadonlySet<string> = new Set([
  'Alt',
  'Meta',
]);

const isModifierOnlyChord = (chord: string[]): boolean =>
  chord.length > 0 && chord.every((token) => NEUTRAL_MODIFIERS.has(token));

/** Re-sort the modifiers portion of a chord into the canonical order. */
const canonicalise = (chord: string[]): string[] => {
  if (chord.length === 0) return chord;
  const last = chord[chord.length - 1] as string;
  const lastIsMod = NEUTRAL_MODIFIERS.has(last);
  const mods = lastIsMod ? chord : chord.slice(0, -1);
  const key = lastIsMod ? null : last;
  const sortedMods = [...mods].sort(
    (a: string, b: string) => (MOD_ORDER[a] ?? 99) - (MOD_ORDER[b] ?? 99)
  );
  return key === null ? sortedMods : [...sortedMods, key];
};

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v: string, i: number) => v === b[i]);

const MAC_RESERVED_CANONICAL: string[][] = macReservedList.map(canonicalise);
const WINDOWS_RESERVED_CANONICAL: string[][] = windowsReservedList.map(canonicalise);

/**
 * Returns true if the chord matches a reserved system combo on the given
 * platform. The web layer currently has no denylist; reserved-check is a no-op
 * (existing behaviour).
 *
 * Sided modifier names are normalised before comparison so a single denylist
 * entry like `Alt+F4` matches both `LeftAlt+F4` and `RightAlt+F4`. The OS
 * reserves these combos for either side, so dictation shouldn't claim either.
 */
export const isReservedSystemHotkey = (
  platform: DictationHotkeyPlatform,
  chord: string[]
): boolean => {
  if (chord.length === 0) return false;
  const target = canonicalise(stripChordSides(chord));

  // Hard-block terminal keys. Two tiers:
  //   - ALWAYS_BLOCKED: rejected as the terminal key regardless of modifiers
  //     (CapsLock, ArrowUp/Down/Left/Right).
  //   - BARE_BLOCKED: rejected only when the chord is just that key alone
  //     (Enter, NumpadEnter, Escape, Tab, Backspace, Delete) — bindable with
  //     any modifier.
  if (platform === 'mac' || platform === 'windows') {
    const last = target[target.length - 1] as string;
    if (!NEUTRAL_MODIFIERS.has(last)) {
      if (ALWAYS_BLOCKED_TERMINAL_KEYS.has(last)) return true;
      if (target.length === 1 && BARE_BLOCKED_TERMINAL_KEYS.has(last)) return true;
    }
  }

  if (
    platform === 'windows' &&
    isModifierOnlyChord(target) &&
    target.some((token) => WINDOWS_MODIFIER_ONLY_SYSTEM_MODIFIERS.has(token))
  ) {
    return true;
  }

  const list =
    platform === 'mac'
      ? MAC_RESERVED_CANONICAL
      : platform === 'windows'
        ? WINDOWS_RESERVED_CANONICAL
        : null;
  if (!list) return false;
  return list.some((entry: string[]) => arraysEqual(entry, target));
};

/**
 * Keys that must not appear as a standalone hotkey. Covers printable-character
 * keys (the user couldn't reliably type the character anymore) and CapsLock
 * (state-toggle key; behaves as a modifier on macOS but isn't usable alone on
 * either platform). Functional keys (Tab, Enter, Escape, Backspace, F-keys,
 * arrows, navigation) are excluded intentionally: safe as standalone hotkeys.
 */
const CHARACTER_TYPING_KEYS: ReadonlySet<string> = new Set<string>([
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((c: string): string => `Key${c}`),
  ...'0123456789'.split('').map((d: string): string => `Digit${d}`),
  'Backquote',
  'Minus',
  'Equal',
  'BracketLeft',
  'BracketRight',
  'Backslash',
  'Semicolon',
  'Quote',
  'Comma',
  'Period',
  'Slash',
  'IntlBackslash',
  'Space',
  'CapsLock',
  'Numpad0',
  'Numpad1',
  'Numpad2',
  'Numpad3',
  'Numpad4',
  'Numpad5',
  'Numpad6',
  'Numpad7',
  'Numpad8',
  'Numpad9',
  'NumpadDecimal',
  'NumpadAdd',
  'NumpadSubtract',
  'NumpadMultiply',
  'NumpadDivide',
  'NumpadComma',
  'NumpadEqual',
]);

/** Returns true if the chord is exactly one character-typing key. */
export const isSingleCharacterKey = (chord: string[]): boolean =>
  chord.length === 1 && CHARACTER_TYPING_KEYS.has(chord[0] ?? '');
